/**
 * DocumentVsReferenceComparator.ts
 * Fase 5 del flujo de procesamiento: Validación cruzada documento vs. referencia
 *
 * @description Compara coordenadas extraídas del documento PTEL con coordenadas
 * de referencia obtenidas de fuentes oficiales (DERA, geocodificación).
 *
 * @version 1.0.0
 * @task ARCH-03
 */

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type ComparisonFlag =
  | 'VALIDADO' // Caso A: distancia ≤25m
  | 'REVISION_MENOR' // Caso A: 25m < distancia ≤100m
  | 'USAR_REFERENCIA' // Caso A: 100m < distancia ≤500m
  | 'REVISION_MANUAL' // Caso A: distancia >500m
  | 'GEOCODIFICADO' // Caso B: sin coords documento, referencia confiable
  | 'BAJA_CONFIANZA' // Caso B: sin coords documento, referencia dudosa
  | 'SIN_COORDENADAS'; // Caso C: sin ninguna coordenada

export type ReferenceSource =
  | 'DERA'
  | 'CartoCiudad'
  | 'CDAU'
  | 'WFS'
  | 'Nominatim';

export interface DocumentCoordinates {
  /** Coordenadas extraídas del documento PTEL (null si no existen) */
  x: number | null;
  y: number | null;
  /** Sistema de referencia original (antes de transformar) */
  crsOriginal?: string;
  /** Ya transformadas a EPSG:25830 */
  crs: 'EPSG:25830';
}

export interface ReferenceCoordinates {
  /** Coordenadas de fuente oficial */
  x: number;
  y: number;
  /** Fuente de las coordenadas */
  fuente: ReferenceSource;
  /** Confianza de la geocodificación (0-100) */
  confianza: number;
  /** Nombre oficial encontrado */
  nombreOficial?: string;
}

export interface ComparisonInput {
  /** ID único de la infraestructura */
  id: string;
  /** Nombre de la infraestructura */
  nombre: string;
  /** Coordenadas del documento (pueden ser null) */
  documento: DocumentCoordinates;
  /** Coordenadas de referencia (pueden ser null) */
  referencia: ReferenceCoordinates | null;
  /** Código INE del municipio */
  codigoINE: string;
  /**
   * PUNTOMEDIO-a («documento primero»): fuentes consultadas por la validación cruzada
   * cuando DISCREPAN entre sí. Entonces la `referencia` es su punto medio, un punto que
   * no dio ninguna fuente, y no debe corregir el documento: el documento se juzga contra
   * la fuente más cercana y, si ninguna lo confirma, se conserva y va a revisión.
   */
  fuentesEnDisputa?: ReadonlyArray<{ x: number; y: number; fuente?: string }> | null;
  /**
   * PUNTOMEDIO-b (DOP-1 c, Luis 1-nov-2026): fuentes que respaldan la referencia, con lo
   * que cada una encontró. Si llega (aunque vacío), entre 100 y 500 m la referencia solo
   * sustituye al documento con ≥ 2 fuentes limpias, independientes y concordantes
   * (`respaldoParaSustituir`); si no, se conserva el documento con 70. Sin el campo
   * (llamadores sin consumidor en producción: `useGeocoding`, `CoordinateProcessingPipeline`)
   * se comporta como antes (DOP-4 a).
   */
  fuentesRespaldo?: ReadonlyArray<FuenteRespaldo> | null;
  /** PUNTOMEDIO-b: municipio del elemento, para no tomar su nombre por el del sitio. */
  municipio?: string;
}

/** PUNTOMEDIO-b: una fuente consultada y lo que encontró. */
export interface FuenteRespaldo {
  x: number;
  y: number;
  fuente: string;
  nombreEncontrado?: string | null;
}

export interface ComparisonResult {
  /** ID de la infraestructura */
  id: string;
  /** Nombre original */
  nombre: string;
  /** Caso de comparación (A, B, C) */
  caso: 'A' | 'B' | 'C';
  /** Flag resultante */
  flag: ComparisonFlag;
  /** Score final (0-100) */
  score: number;
  /** Coordenadas finales recomendadas */
  coordenadasFinales: { x: number; y: number } | null;
  /** Distancia entre documento y referencia (solo Caso A) */
  distanciaMetros: number | null;
  /** Fuente de las coordenadas finales */
  fuenteCoordenadas: 'documento' | 'referencia' | 'ninguna';
  /** Metadatos adicionales */
  metadata: {
    tieneCoordenadasDocumento: boolean;
    tieneCoordenadasReferencia: boolean;
    confianzaReferencia: number | null;
    fuenteReferencia: string | null;
    /** PUNTOMEDIO-a: el documento se juzgó contra fuentes en disputa (no contra su punto medio) */
    referenciaEnDisputa?: boolean;
    /** PUNTOMEDIO-b: entre 100 y 500 m, la referencia no tenía respaldo para sustituir al documento */
    sinRespaldoParaSustituir?: boolean;
  };
}

export interface BatchStats {
  total: number;
  casoA: number;
  casoB: number;
  casoC: number;
  validados: number;
  requierenRevision: number;
}

export interface BatchResult {
  results: ComparisonResult[];
  stats: BatchStats;
}

// ============================================================================
// UMBRALES CONFIGURABLES
// ============================================================================

export const DISTANCE_THRESHOLDS = {
  /** Distancia máxima para considerar match exacto */
  VALIDADO: 25,
  /** Distancia máxima para revisión menor */
  REVISION_MENOR: 100,
  /** Distancia máxima para usar referencia */
  USAR_REFERENCIA: 500,
} as const;

export const CONFIDENCE_THRESHOLD = {
  /** Umbral mínimo de confianza para considerar referencia fiable */
  CONFIABLE: 70,
} as const;

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Calcula distancia euclidiana en metros para coordenadas UTM.
 * En UTM, las unidades son metros, por lo que la distancia euclidiana
 * es directamente la distancia en metros.
 */
export function calcularDistanciaUTM(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Determina si las coordenadas del documento son válidas (no null).
 */
function tieneCoordenadasDocumento(doc: DocumentCoordinates): boolean {
  return doc.x !== null && doc.y !== null;
}

/**
 * Determina si hay coordenadas de referencia disponibles.
 */
function tieneCoordenadasReferencia(
  ref: ReferenceCoordinates | null
): ref is ReferenceCoordinates {
  return ref !== null && ref.x !== undefined && ref.y !== undefined;
}

// ============================================================================
// PUNTOMEDIO-b: ¿HAY RESPALDO PARA SUSTITUIR AL DOCUMENTO?
// ============================================================================

/** Distancia máxima entre fuentes limpias para que concuerden (m). */
export const RESPALDO_CONCORDANCIA_M = 100;
/** Por debajo de esta distancia (m) dos fuentes dan el mismo dato (copiado). */
const RESPALDO_MISMO_DATO_M = 1;

/** Palabras de tipo de instalación: no identifican un sitio concreto. */
const PALABRAS_GENERICAS = new Set(
  (
    'centro colegio publico escuela infantil primaria instituto educacion secundaria iglesia ' +
    'parroquia ermita cortijo farmacia supermercado polideportivo municipal pabellon deportivo ' +
    'campo futbol piscina consultorio salud ayuntamiento oficina estacion servicio telefonia movil ' +
    'depuradora deposito captacion nave planta industrial poligono empresa hermanos calle avenida ' +
    'plaza camino carretera paraje barrio barriada urbanizacion residencia mayores hogar local ' +
    'social cultural casa rural restaurante hotel hostal parque jardin cementerio tanatorio ' +
    'guardia civil policia bomberos almacen taller construcciones servicios sociedad limitada'
  ).split(' ')
);
const PALABRAS_VACIAS = new Set('del los las una para con por que sin sobre entre desde hasta'.split(' '));
/** Lo encontrado es una dirección (vía, portal, polígono…), no el sitio. */
const ES_DIRECCION =
  /^(calle|avenida|avda|plaza|camino|carretera|ctra|poligono|paseo|barrio|barriada|urbanizacion|travesia|ronda|cuesta|callejon|pasaje|carril|lugar|paraje|diseminado)\b/;
const PORTAL_CON_CP = /, ?\d+ - \d{5}/;

function plano(s: string | null | undefined): string {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function palabrasSignificativas(s: string | null | undefined): Set<string> {
  return new Set(
    plano(s)
      .split(' ')
      .filter((w) => w.length >= 4 && !PALABRAS_VACIAS.has(w) && !PALABRAS_GENERICAS.has(w))
  );
}

/**
 * PUNTOMEDIO-b: ¿encontró esta fuente el sitio buscado? No lo encontró si lo hallado es una
 * dirección o portal o el nombre del municipio, ni si no comparte ninguna palabra significativa
 * con el nombre buscado. Catastro y las pedanías de NGA no llegan como fuentes con nombre
 * (medido en la pasada H1 del 1-nov-2026): sin fuentes, no hay respaldo.
 */
export function esFuenteLimpia(nombreBuscado: string, municipio: string | undefined, f: FuenteRespaldo): boolean {
  const encontrado = plano(f.nombreEncontrado);
  if (!encontrado) return false;
  if (ES_DIRECCION.test(encontrado) || PORTAL_CON_CP.test(String(f.nombreEncontrado))) return false;
  if (municipio && encontrado === plano(municipio)) return false;
  const buscadas = palabrasSignificativas(nombreBuscado);
  for (const w of palabrasSignificativas(f.nombreEncontrado)) if (buscadas.has(w)) return true;
  return false;
}

/**
 * PUNTOMEDIO-b (DOP-1 c): la referencia puede sustituir al documento solo si la respaldan
 * ≥ 2 fuentes limpias (`esFuenteLimpia`), independientes (las que dan la misma coordenada,
 * a menos de 1 m, cuentan una vez: es un dato copiado) y concordantes (≤ 100 m entre sí).
 */
export function respaldoParaSustituir(
  nombreBuscado: string,
  municipio: string | undefined,
  fuentes: ReadonlyArray<FuenteRespaldo> | null | undefined
): { limpias: number; sustituye: boolean } {
  const independientes: FuenteRespaldo[] = [];
  for (const f of fuentes ?? []) {
    if (!Number.isFinite(f.x) || !Number.isFinite(f.y) || f.x === 0 || f.y === 0) continue;
    if (!esFuenteLimpia(nombreBuscado, municipio, f)) continue;
    if (independientes.every((g) => calcularDistanciaUTM(f.x, f.y, g.x, g.y) >= RESPALDO_MISMO_DATO_M)) {
      independientes.push(f);
    }
  }
  const concuerdan = independientes.every((a) =>
    independientes.every((b) => calcularDistanciaUTM(a.x, a.y, b.x, b.y) <= RESPALDO_CONCORDANCIA_M)
  );
  return { limpias: independientes.length, sustituye: independientes.length >= 2 && concuerdan };
}

// ============================================================================
// FUNCIÓN PRINCIPAL: compareCoordinates
// ============================================================================

/**
 * Compara coordenadas del documento vs. referencia oficial.
 *
 * @param input - Datos de entrada con coordenadas documento y referencia
 * @returns Resultado de la comparación con flag, score y coordenadas finales
 */
export function compareCoordinates(input: ComparisonInput): ComparisonResult {
  const { id, nombre, documento, referencia } = input;

  const hasDocCoords = tieneCoordenadasDocumento(documento);
  const hasRefCoords = tieneCoordenadasReferencia(referencia);

  // Metadatos base
  const metadata = {
    tieneCoordenadasDocumento: hasDocCoords,
    tieneCoordenadasReferencia: hasRefCoords,
    confianzaReferencia: hasRefCoords ? referencia.confianza : null,
    fuenteReferencia: hasRefCoords ? referencia.fuente : null,
  };

  // ─────────────────────────────────────────────────────────────────────
  // CASO C: Sin ninguna coordenada
  // ─────────────────────────────────────────────────────────────────────
  if (!hasDocCoords && !hasRefCoords) {
    return {
      id,
      nombre,
      caso: 'C',
      flag: 'SIN_COORDENADAS',
      score: 0,
      coordenadasFinales: null,
      distanciaMetros: null,
      fuenteCoordenadas: 'ninguna',
      metadata,
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // CASO B: Documento NO tiene coordenadas, pero SÍ hay referencia
  // ─────────────────────────────────────────────────────────────────────
  if (!hasDocCoords && hasRefCoords) {
    const esConfiable = referencia.confianza >= CONFIDENCE_THRESHOLD.CONFIABLE;

    return {
      id,
      nombre,
      caso: 'B',
      flag: esConfiable ? 'GEOCODIFICADO' : 'BAJA_CONFIANZA',
      score: referencia.confianza,
      coordenadasFinales: { x: referencia.x, y: referencia.y },
      distanciaMetros: null,
      fuenteCoordenadas: 'referencia',
      metadata,
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // CASO A: Documento SÍ tiene coordenadas
  // ─────────────────────────────────────────────────────────────────────
  // En este punto, hasDocCoords es true (documento.x y documento.y no son null)
  const docX = documento.x as number;
  const docY = documento.y as number;

  // Subcaso A.1: Documento tiene coords, pero NO hay referencia
  if (!hasRefCoords) {
    // Sin referencia para validar, usamos documento con score moderado
    return {
      id,
      nombre,
      caso: 'A',
      flag: 'REVISION_MENOR', // Sin referencia, requiere revisión
      score: 70, // Score moderado por falta de validación
      coordenadasFinales: { x: docX, y: docY },
      distanciaMetros: null,
      fuenteCoordenadas: 'documento',
      metadata,
    };
  }

  // PUNTOMEDIO-a · fuentes en disputa: nunca se corrige el documento con su punto medio.
  // Se juzga contra la fuente más cercana: ≤25 m VALIDADO, ≤100 m REVISION_MENOR y, más
  // lejos, REVISION_MANUAL con el documento conservado (rama de revisión de los llamadores).
  const enDisputa = (input.fuentesEnDisputa ?? []).filter(
    (f) => Number.isFinite(f.x) && Number.isFinite(f.y) && f.x !== 0 && f.y !== 0
  );
  if (enDisputa.length > 0) {
    const masCercana = Math.min(
      ...enDisputa.map((f) => calcularDistanciaUTM(docX, docY, f.x, f.y))
    );
    const metaDisputa = { ...metadata, referenciaEnDisputa: true };
    if (masCercana <= DISTANCE_THRESHOLDS.VALIDADO) {
      return {
        id, nombre, caso: 'A', flag: 'VALIDADO', score: 100,
        coordenadasFinales: { x: docX, y: docY }, distanciaMetros: masCercana,
        fuenteCoordenadas: 'documento', metadata: metaDisputa,
      };
    }
    if (masCercana <= DISTANCE_THRESHOLDS.REVISION_MENOR) {
      return {
        id, nombre, caso: 'A', flag: 'REVISION_MENOR', score: 80,
        coordenadasFinales: { x: docX, y: docY }, distanciaMetros: masCercana,
        fuenteCoordenadas: 'documento', metadata: metaDisputa,
      };
    }
    return {
      id, nombre, caso: 'A', flag: 'REVISION_MANUAL', score: 30,
      coordenadasFinales: null, distanciaMetros: masCercana,
      fuenteCoordenadas: 'ninguna', metadata: metaDisputa,
    };
  }

  // Subcaso A.2: Ambos tienen coordenadas - Comparar distancia
  const distancia = calcularDistanciaUTM(docX, docY, referencia.x, referencia.y);

  if (distancia <= DISTANCE_THRESHOLDS.VALIDADO) {
    // ✅ MATCH EXACTO - Usar coordenadas documento (más precisas)
    return {
      id,
      nombre,
      caso: 'A',
      flag: 'VALIDADO',
      score: 100,
      coordenadasFinales: { x: docX, y: docY },
      distanciaMetros: distancia,
      fuenteCoordenadas: 'documento',
      metadata,
    };
  }

  if (distancia <= DISTANCE_THRESHOLDS.REVISION_MENOR) {
    // ⚠️ DESVIACIÓN MENOR - Revisar, posible error de digitalización
    return {
      id,
      nombre,
      caso: 'A',
      flag: 'REVISION_MENOR',
      score: 80,
      coordenadasFinales: { x: docX, y: docY },
      distanciaMetros: distancia,
      fuenteCoordenadas: 'documento',
      metadata,
    };
  }

  if (
    distancia <= DISTANCE_THRESHOLDS.USAR_REFERENCIA &&
    input.fuentesRespaldo !== undefined &&
    !respaldoParaSustituir(nombre, input.municipio, input.fuentesRespaldo).sustituye
  ) {
    // PUNTOMEDIO-b (DOP-1 c, nota 70 · Luis 1-nov-2026): sin dos fuentes limpias que lo
    // respalden, el documento se conserva. Verificado en 14 casos: el plan estaba a 1-72 m
    // del sitio real en 9 de 10 resueltos y la referencia lo llevaba a 100-460 m.
    return {
      id,
      nombre,
      caso: 'A',
      flag: 'REVISION_MENOR',
      score: 70,
      coordenadasFinales: { x: docX, y: docY },
      distanciaMetros: distancia,
      fuenteCoordenadas: 'documento',
      metadata: { ...metadata, sinRespaldoParaSustituir: true },
    };
  }

  if (distancia <= DISTANCE_THRESHOLDS.USAR_REFERENCIA) {
    // ⚠️ DESVIACIÓN SIGNIFICATIVA - Probable error en documento
    return {
      id,
      nombre,
      caso: 'A',
      flag: 'USAR_REFERENCIA',
      score: 60,
      coordenadasFinales: { x: referencia.x, y: referencia.y },
      distanciaMetros: distancia,
      fuenteCoordenadas: 'referencia',
      metadata,
    };
  }

  // ❌ DESVIACIÓN GRAVE - Posible infraestructura diferente
  return {
    id,
    nombre,
    caso: 'A',
    flag: 'REVISION_MANUAL',
    score: 30,
    coordenadasFinales: null, // No recomendamos ninguna, requiere revisión humana
    distanciaMetros: distancia,
    fuenteCoordenadas: 'ninguna',
    metadata,
  };
}

// ============================================================================
// FUNCIÓN BATCH: compareBatch
// ============================================================================

/**
 * Compara múltiples infraestructuras en lote.
 *
 * @param inputs - Array de datos de entrada
 * @returns Resultados y estadísticas agregadas
 */
export function compareBatch(inputs: ComparisonInput[]): BatchResult {
  const results = inputs.map(compareCoordinates);

  const stats: BatchStats = {
    total: results.length,
    casoA: results.filter((r) => r.caso === 'A').length,
    casoB: results.filter((r) => r.caso === 'B').length,
    casoC: results.filter((r) => r.caso === 'C').length,
    validados: results.filter((r) => r.flag === 'VALIDADO').length,
    requierenRevision: results.filter(
      (r) =>
        r.flag === 'REVISION_MENOR' ||
        r.flag === 'REVISION_MANUAL' ||
        r.flag === 'BAJA_CONFIANZA'
    ).length,
  };

  return { results, stats };
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  compareCoordinates,
  compareBatch,
  calcularDistanciaUTM,
  DISTANCE_THRESHOLDS,
  CONFIDENCE_THRESHOLD,
};
